import { effectiveState } from './overlayMerge';
import { BUG_CVE_REVIEW_STATES, CONFIG_REMOVED, AUTO_CONFIG_REMOVED } from './reviewStates';

const UNKNOWN = 'Unknown';

/**
 * How many issues sit in each review state, for the Bug/CVE Review Distribution
 * panels.
 *
 * Counts against the state each issue is actually *displaying*, not the state
 * stored for it: the same `effectiveState` the table rows call, over the same
 * three inputs. That is the whole point — a panel that re-derived the merge rule
 * could disagree with the list right underneath it, and a reviewer who catches
 * the two contradicting each other stops believing either. It also means an
 * override lands without a reload, because the counts are derived on the same
 * render pass as the row that changed.
 *
 * @param issues          the version's bugs or CVEs, each with an `id`
 * @param humanStates     stored human states keyed by issue id (`bugStates` / `cveStates`)
 * @param overlaySection  computed auto-marks keyed by issue id (`overlay?.bugs` / `overlay?.cves`);
 *                        absent on an ordinary non-inventory project, which is fine
 * @returns `{ distribution: [{ state, label, count, autoCount }], total }`
 */
export function getReviewDistribution(issues, humanStates, overlaySection) {
  if (!Array.isArray(issues) || issues.length === 0) {
    return { distribution: [], total: 0 };
  }

  const counts = new Map();
  let autoConfigRemoved = 0;
  let unknown = 0;

  const known = new Set(BUG_CVE_REVIEW_STATES.map(s => s.value));

  issues.forEach(issue => {
    const { value } = effectiveState(issue.id, humanStates, overlaySection);

    // The automatic flavour is never stored and never offered as a choice, so it
    // is an implementation detail to a reviewer: it belongs in the same row as a
    // manual mark, contributing only to the count named in that row's label.
    if (value === AUTO_CONFIG_REMOVED) {
      autoConfigRemoved += 1;
      counts.set(CONFIG_REMOVED, (counts.get(CONFIG_REMOVED) || 0) + 1);
      return;
    }

    if (!known.has(value)) {
      unknown += 1;
      return;
    }

    counts.set(value, (counts.get(value) || 0) + 1);
  });

  // Vocabulary order, dropping the states nobody used — eight rows of mostly
  // zeros is what this panel exists to avoid.
  const distribution = BUG_CVE_REVIEW_STATES.filter(({ value }) => counts.get(value) > 0).map(({ value }) => {
    const count = counts.get(value);
    const autoCount = value === CONFIG_REMOVED ? autoConfigRemoved : 0;
    return {
      state: value,
      label: autoCount > 0 ? `${value} (${autoCount} auto)` : value,
      count,
      autoCount
    };
  });

  // A state the backend accepts but the vocabulary here omits would otherwise
  // vanish, leaving the rows short of the total the headline card shows. Keep it
  // visible instead — the same defensive move the severity panels make for a
  // missing severity.
  if (unknown > 0) {
    distribution.push({ state: UNKNOWN, label: UNKNOWN, count: unknown, autoCount: 0 });
  }

  return { distribution, total: issues.length };
}
